import * as React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Editor, EditorUtils, EditorTools, ProseMirror } from '@progress/kendo-react-editor';
import { Schema, keymap } from '@progress/kendo-editor-common';
import { accentUtils, t } from '../../services/HelperService';
import { AccentLabel } from '../AccentLabel';

const { EditorState, EditorView } = ProseMirror;

const {
    Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
    Undo, Redo
} = EditorTools;

// The intersection of real HTML and the pseudo-HTML markup DevExpress report text
// controls render with AllowMarkupText: <b> <i> <u> <s> <sub> <sup> <br>.
// (DevExpress colour/size/font tags use non-standard syntax, so they are excluded.)
const ALLOWED_TAGS = new Set(['B', 'I', 'U', 'S', 'SUB', 'SUP', 'BR']);

// Editor/paste equivalents normalized to the DevExpress-safe tag names.
const TAG_RENAMES = { STRONG: 'b', EM: 'i', DEL: 's', STRIKE: 's' };

// Block-level elements are flattened to <br> separators — DevExpress markup has no blocks.
const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'TR']);

/// Returns the plain text of basic-HTML content — for one-line grid/list snippets.
export const stripBasicHtml = (html) => {
    if (accentUtils.isEmpty(html)) return '';

    const element = document.createElement('div');
    element.innerHTML = html;
    return (element.textContent ?? '').trim();
};

/// Reduces arbitrary HTML to the DevExpress-report-safe subset: only b/i/u/s/sub/sup/br
/// survive (strong/em/del/strike are renamed), block elements become <br> separators,
/// and everything else is unwrapped to its text content.
export const sanitizeBasicHtml = (html) => {
    if (accentUtils.isEmpty(html)) return '';

    const source = document.createElement('div');
    source.innerHTML = html;

    const target = document.createElement('div');

    const appendChildren = (srcNode, destNode) => {
        srcNode.childNodes.forEach(child => {
            if (child.nodeType === Node.TEXT_NODE) {
                destNode.appendChild(document.createTextNode(child.textContent ?? ''));
                return;
            }

            if (child.nodeType !== Node.ELEMENT_NODE) return;

            const tag = child.tagName;

            if (tag === 'BR') {
                destNode.appendChild(document.createElement('br'));
                return;
            }

            const renamed = TAG_RENAMES[tag];
            const allowedName = renamed ?? (ALLOWED_TAGS.has(tag) ? tag.toLowerCase() : null);

            if (allowedName) {
                const element = document.createElement(allowedName);
                destNode.appendChild(element);
                appendChildren(child, element);
            } else {
                // Disallowed element — unwrap its children; block elements end with a line break.
                appendChildren(child, destNode);
                if (BLOCK_TAGS.has(tag)) {
                    destNode.appendChild(document.createElement('br'));
                }
            }
        });
    };

    appendChildren(source, target);

    // Trim trailing line breaks left by the last block element.
    while (target.lastChild && target.lastChild.nodeName === 'BR') {
        target.removeChild(target.lastChild);
    }

    return target.innerHTML;
};

const hasTextContent = html => !accentUtils.isEmpty(stripBasicHtml(html));

// DevExpress markup has no list tags, so bullets are literal characters (matching how
// report disclosures are authored, e.g. "● The size of the control mechanism...").
const InsertBullet = ({ view }) => (
    <Button
        onClick={() => {
            if (!view) return;
            view.dispatch(view.state.tr.insertText('● ').scrollIntoView());
            view.focus();
        }}
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.preventDefault()}
        title="Insert bullet"
    >●</Button>
);

/// Basic HTML editor constrained to the markup DevExpress report text controls support.
/// The restricted ProseMirror schema is the sanitizer for typing/pasting; the output is
/// additionally normalized through sanitizeBasicHtml so stored values are report-safe.
export const AccentHtmlBasicUnbound = React.forwardRef((props, ref) => {

    const ctrl = React.useRef();
    const [hasText, setHasText] = React.useState(hasTextContent(props.defaultValue));

    const publish = React.useCallback((rawHtml) => {
        if (!props.onChange) return;

        const sanitized = sanitizeBasicHtml(rawHtml);
        const isEmpty = accentUtils.isEmpty(stripBasicHtml(sanitized));

        setHasText(!isEmpty);
        props.onChange(isEmpty ? null : sanitized);
    }, [props]);

    React.useImperativeHandle(ref, () => ({
        getHtml: () => sanitizeBasicHtml(ctrl.current?.view?.dom?.innerHTML ?? '')
    }), []);

    const onEditorBlur = (_view, _domEvent) => {
        publish(ctrl.current?.view?.dom?.innerHTML ?? '');
    };

    const onMount = (event) => {
        const { viewProps } = event;
        const { plugins, schema } = viewProps.state;

        const pick = (map, names) => names.reduce((acc, name) => {
            const spec = map.get(name);
            if (spec) acc[name] = spec;
            return acc;
        }, {});

        // Restrict the default Kendo schema to the DevExpress-safe subset. Both the Kendo
        // mark names and their HTML aliases are listed so whichever the installed version
        // defines is kept; the tools resolve via mark/altMarks either way.
        const restrictedSchema = new Schema({
            nodes: pick(schema.spec.nodes, ['doc', 'paragraph', 'text', 'hard_break']),
            marks: pick(schema.spec.marks, ['b', 'strong', 'i', 'em', 'u', 'del', 's', 'strike', 'sub', 'sup'])
        });

        const doc = EditorUtils.createDocument(restrictedSchema, props.defaultValue ?? '');

        // Enter inserts a <br> (hard_break) rather than splitting into a new <p> —
        // DevExpress markup has no paragraphs, so line breaks are the newline model.
        // Prepended so it wins over the default Enter (split block) binding.
        const enterAsLineBreak = keymap({
            'Enter': (state, dispatch) => {
                const br = state.schema.nodes.hard_break;
                if (!br) return false;
                if (dispatch) {
                    dispatch(state.tr.replaceSelectionWith(br.create()).scrollIntoView());
                }
                return true;
            }
        });

        return new EditorView(
            { mount: event.dom }, {
            ...viewProps,
            state: EditorState.create({ doc, plugins: [enterAsLineBreak, ...plugins] }),
            handleDOMEvents: {
                ...(viewProps.handleDOMEvents || {}),
                'blur': onEditorBlur
            }
        });
    };

    const onChange = (event) => {
        publish(event.html);
    };

    const tools = [
        [Bold, Italic, Underline, Strikethrough],
        [Subscript, Superscript],
        [InsertBullet],
        [Undo, Redo]
    ];

    const height = accentUtils.isNull(props.height) ? 120 : props.height;

    const isValid = !props.required || hasText;
    const className = isValid ? 'accentHtml' : 'accentHtml invalid';

    const editor = <Editor
        ref={ctrl}
        defaultContent={props.defaultValue ?? ''}
        defaultEditMode="div"
        onChange={onChange}
        tools={tools}
        onMount={onMount}
        contentStyle={{ height: height }}
    />;

    return (<div className={className} data-tagname={props.dataTagName}>
        {accentUtils.isEmpty(props.label) ? editor : <AccentLabel text={t(props.label)}>{editor}</AccentLabel>}
    </div>);
});
